import React, { useState, useEffect } from 'react';
import { 
  Form, 
  Input, 
  Button, 
  Select, 
  Row, 
  Col, 
  Typography, 
  DatePicker, 
  Breadcrumb, 
  Modal, 
  message, 
  InputNumber, 
  Descriptions, 
  Checkbox,
  Card,
  Space,
  Tag,
  Alert,
  Tooltip,
  Steps
} from 'antd';
import {
  BuildOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  SettingOutlined,
  FileTextOutlined,
  SaveOutlined,
  EyeOutlined,
  EditOutlined,
  PlusOutlined,
  HomeOutlined,
  InfoCircleOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  BankOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import moment from 'moment';
import { postJob, updateJob } from '../../api';
import './PostJob.css';

const { Option } = Select;
const { Title, Text } = Typography;
const { Step } = Steps;

const states = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia', 
  'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 
  'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 
  'West Virginia', 'Wisconsin', 'Wyoming'
];

const DocumentType = {
  ACLS: 'ACLS',
  BLS: 'BLS',
  NIHSS: 'NIHSS',
  TNCC: 'TNCC',
  Resume: 'Resume',
  ReliasChecklist: 'Relias-Checklist',
  ID: 'ID',
  RNLicense: 'RN-License',
};

const generateRandomCode = () => {
  return `JOB-${Math.floor(100000 + Math.random() * 900000)}`;
};

const PostJobScreen = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const [previewVisible, setPreviewVisible] = useState(false);
  const [mandatoryDocuments, setMandatoryDocuments] = useState([]);
  const [jobData, setJobData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const isEditMode = location.state?.job != null;

  useEffect(() => {
    if (isEditMode) {
      const job = location.state.job;
      form.setFieldsValue({
        jobTitle: job.title,
        jobCategory: job.category,
        code: job.code,
        statusReason: job.statusReason,
        reasonDescription: job.reason?.description,
        reasonDetails: job.reason?.details,
        manager: job.managerName,
        department: job.department,
        numberOfPositions: job.numberOfOpenings,
        jobDescription: job.duties,
        requiredSkills: job.requiredSkills?.join(', '),
        preferredSkills: job.preferredSkills?.join(', '),
        languages: job.languages?.join(', '),
        degree: job.degree,
        keywords: job.keywords?.join(', '),
        startDate: moment(job.startDate),
        endDate: moment(job.endDate),
        hoursPerDay: job.hoursPerDay,
        daysPerWeek: job.daysPerWeek,
        float: job.isFloat ? 'yes' : 'no',
        shift: job.shift,
        scheduleNotes: job.scheduleNotes,
        street: job.address?.street,
        city: job.address?.city,
        state: job.address?.state,
        zipCode: job.address?.zipCode,
        baseSalary: job.salary?.base,
        callBackSalary: job.salary?.callBack,
        holidaySalary: job.salary?.holiday,
        onCallSalary: job.salary?.onCall,
        overtimeSalary: job.salary?.overtime,
        regularSalary: job.salary?.regular,
        type: job.type,
        unit: job.unit
      });
      setMandatoryDocuments(job.mandatoryDocuments || []);
    } else {
      form.setFieldsValue({ code: generateRandomCode() });
    }
  }, [form, location.state, isEditMode]);

  const onFinish = (values) => {
    setJobData(values);
    setPreviewVisible(true);
  };

  const handleDocumentChange = (checkedValues) => {
    setMandatoryDocuments(checkedValues);
  };

  const handlePublish = async () => {
    setLoading(true);
    try {
      const jobPayload = {
        title: jobData.jobTitle,
        category: jobData.jobCategory,
        code: jobData.code,
        statusReason: jobData.statusReason,
        reason: {
          description: jobData.reasonDescription,
          details: jobData.reasonDetails
        },
        department: jobData.department,
        keywords: jobData.keywords.split(',').map(keyword => keyword.trim()),
        numberOfOpenings: jobData.numberOfPositions,
        duties: jobData.jobDescription,
        requiredSkills: jobData.requiredSkills.split(',').map(skill => skill.trim()),
        preferredSkills: jobData.preferredSkills.split(',').map(skill => skill.trim()),
        languages: jobData.languages.split(',').map(language => language.trim()),
        degree: jobData.degree,
        managerName: jobData.manager,
        address: {
          street: jobData.street,
          city: jobData.city,
          state: jobData.state,
          zipCode: jobData.zipCode,
          country: "us",
          coordinates: [parseFloat('-122.4194'), parseFloat('37.7749')]
        },
        salary: {
          base: jobData.baseSalary,
          callBack: jobData.callBackSalary,
          holiday: jobData.holidaySalary,
          onCall: jobData.onCallSalary,
          overtime: jobData.overtimeSalary,
          regular: jobData.regularSalary,
          currency: "USD"
        },
        hoursPerDay: jobData.hoursPerDay,
        daysPerWeek: jobData.daysPerWeek,
        scheduleNotes: jobData.scheduleNotes,
        status: "open",
        unit: jobData.unit,
        type: jobData.type,
        isFloat: jobData.float === 'yes',
        shift: jobData.shift,
        startDate: jobData.startDate.format('YYYY-MM-DD'),
        endDate: jobData.endDate.format('YYYY-MM-DD'),
        mandatoryDocuments: mandatoryDocuments,
      };

      if (isEditMode) {
        await updateJob(location.state.job._id, jobPayload);
        message.success('Assignment updated successfully!');
      } else {
        await postJob(jobPayload);
        message.success('Assignment posted successfully!');
      }
      navigate('/employer/job-management');
    } catch (error) {
      message.error(isEditMode ? 'Failed to update assignment' : 'Failed to post assignment');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
      <Breadcrumb style={{ marginBottom: '16px' }}>
            <Breadcrumb.Item>
              <HomeOutlined />
            </Breadcrumb.Item>
        <Breadcrumb.Item>Assignment List</Breadcrumb.Item>
            <Breadcrumb.Item>
              {isEditMode ? <EditOutlined /> : <PlusOutlined />}
              {isEditMode ? 'Edit Assignment' : 'Create New Assignment'}
            </Breadcrumb.Item>
      </Breadcrumb>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <div>
              <Title level={2} style={{ margin: 0, color: '#006971' }}>
                <BuildOutlined style={{ marginRight: '12px' }} />
                {isEditMode ? 'Edit Assignment' : 'Create New Assignment'}
              </Title>
              <Text type="secondary" style={{ fontSize: '16px' }}>
                {isEditMode ? 'Update assignment details and requirements' : 'Fill in the details to create a new assignment'}
              </Text>
            </div>
            <div>
              <Tag color="blue" style={{ padding: '4px 12px', fontSize: '14px' }}>
                {isEditMode ? 'Edit Mode' : 'New Assignment'}
              </Tag>
            </div>
          </div>

        </div>

      <Form
        form={form}
        name="post-job"
        onFinish={onFinish}
        layout="vertical"
        initialValues={{
          jobTitle: 'Operating Room Nurse',
          jobCategory: 'Nursing',
          statusReason: 'New Position',
          reasonDescription: 'Expanding Team',
            reasonDetails: 'Summer Staff UP',
          manager: 'Mark Anderson',
          department: 'Operating Room',
          numberOfPositions: '3',
          jobDescription: 'We are looking for Emergency Room nurses to cover our site at Mt Sinai Beth Israel. 13 week contracts + benefits. Min: BSN + 2 years ED Experience.',
            requiredSkills: 'BSN, 4 years or exp, critical care',
          preferredSkills: 'Telemetry',
          languages: 'English, Spanish',
          degree: "Bachelor's Degree in Nursing",
          keywords: 'Operating Room, NY',
          startDate: moment('2025-01-01', 'YYYY-MM-DD'),
          endDate: moment('2025-12-31', 'YYYY-MM-DD'),
          hoursPerDay: 12,
          daysPerWeek: '3',
          float: 'no',
          shift: 'day',
          scheduleNotes: 'Flexible working hours',
          street: '123 Main St',
          city: 'San Francisco',
          state: 'California',
          zipCode: '94105',
          baseSalary: 120,
          callBackSalary: 140,
          holidaySalary: 220,
          onCallSalary: 150,
          overtimeSalary: 240,
          regularSalary: 120,
          type: 'contract',
          unit: 'NICU'
        }}
      >
          <Row gutter={[24, 24]}>
            {/* Job Description Card */}
            <Col span={24}>
              <Card 
                title={
                  <Space>
                    <BuildOutlined />
                    Job Description
                    <Tooltip title="Basic job information and requirements">
                      <InfoCircleOutlined style={{ color: '#999' }} />
                    </Tooltip>
                  </Space>
                }
                style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={8}>
                    <Form.Item 
                      name="jobTitle" 
                      label="Job Title" 
                      rules={[{ required: true, message: 'Please enter the job title!' }]}
                    >
                      <Input size="large" placeholder="e.g., Operating Room Nurse" style={{ borderRadius: '8px' }} />
                </Form.Item>
              </Col>
                  <Col xs={24} md={8}>
                    <Form.Item 
                      name="jobCategory" 
                      label="Job Category" 
                      rules={[{ required: true, message: 'Please enter the job category!' }]}
                    >
                      <Input size="large" placeholder="e.g., Nursing" style={{ borderRadius: '8px' }} />
                </Form.Item>
              </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="code" label="Assignment Code" hidden>
                  <Input />
                </Form.Item>
              </Col>
            </Row>

                <Row gutter={[16, 16]}>
                  <Col xs={24} md={8}>
                    <Form.Item 
                      name="statusReason" 
                      label="Status Reason" 
                      rules={[{ required: true, message: 'Please enter the status reason!' }]}
                    >
                      <Input size="large" placeholder="e.g., New Position" style={{ borderRadius: '8px' }} />
                </Form.Item>
              </Col>
                  <Col xs={24} md={8}>
                    <Form.Item 
                      name="reasonDescription" 
                      label="Reason Description" 
                      rules={[{ required: true, message: 'Please enter the reason description!' }]}
                    >
                      <Input size="large" placeholder="e.g., Expanding Team" style={{ borderRadius: '8px' }} />
                </Form.Item>
              </Col>
                  <Col xs={24} md={8}>
                    <Form.Item 
                      name="reasonDetails" 
                      label="Reason Details" 
                      rules={[{ required: true, message: 'Please enter the reason details!' }]}
                    >
                      <Input size="large" placeholder="e.g., Summer Staff UP" style={{ borderRadius: '8px' }} />
                </Form.Item>
              </Col>
            </Row>

                <Row gutter={[16, 16]}>
                  <Col xs={24} md={8}>
                    <Form.Item 
                      name="manager" 
                      label="Hiring Manager" 
                      rules={[{ required: true, message: 'Please enter the manager!' }]}
                    >
                      <Input 
                        size="large" 
                        placeholder="Manager Name" 
                        prefix={<TeamOutlined />}
                        style={{ borderRadius: '8px' }} 
                      />
                </Form.Item>
              </Col>
                  <Col xs={24} md={8}>
                    <Form.Item 
                      name="department" 
                      label="Department" 
                      rules={[{ required: true, message: 'Please enter the department!' }]}
                    >
                      <Input 
                        size="large" 
                        placeholder="Department" 
                        prefix={<BankOutlined />}
                        style={{ borderRadius: '8px' }} 
                      />
                </Form.Item>
              </Col>
                  <Col xs={24} md={8}>
                    <Form.Item 
                      name="numberOfPositions" 
                      label="Number of Positions" 
                      rules={[{ required: true, message: 'Please select the number of positions!' }]}
                    >
                      <Select size="large" placeholder="Select positions" style={{ borderRadius: '8px' }}>
                        <Option value="1">1 Position</Option>
                        <Option value="2">2 Positions</Option>
                        <Option value="3">3 Positions</Option>
                        <Option value="10">10 Positions</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

                <Form.Item 
                  name="jobDescription" 
                  label="Job Description" 
                  rules={[{ required: true, message: 'Please enter the job description!' }]}
                >
                  <Input.TextArea 
                    rows={4} 
                    placeholder="Describe the role, responsibilities, and requirements..."
                    style={{ borderRadius: '8px' }}
                  />
                </Form.Item>

                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <Form.Item 
                      name="requiredSkills" 
                      label="Required Skills" 
                      rules={[{ required: true, message: 'Please enter the required skills!' }]}
                    >
                      <Input 
                        size="large"
                        placeholder="BSN, 4 years exp, critical care (comma separated)" 
                        style={{ borderRadius: '8px' }}
                      />
                </Form.Item>
              </Col>
                  <Col xs={24} md={12}>
                    <Form.Item 
                      name="preferredSkills" 
                      label="Preferred Skills" 
                      rules={[{ required: true, message: 'Please enter the preferred skills!' }]}
                    >
                      <Input 
                        size="large"
                        placeholder="Telemetry, ICU experience (comma separated)" 
                        style={{ borderRadius: '8px' }}
                      />
                </Form.Item>
              </Col>
            </Row>

                <Row gutter={[16, 16]}>
                  <Col xs={24} md={8}>
                    <Form.Item 
                      name="languages" 
                      label="Languages" 
                      rules={[{ required: true, message: 'Please enter the languages!' }]}
                    >
                      <Input 
                        size="large"
                        placeholder="English, Spanish (comma separated)" 
                        style={{ borderRadius: '8px' }}
                      />
                </Form.Item>
              </Col>
                  <Col xs={24} md={8}>
                    <Form.Item 
                      name="degree" 
                      label="Required Degree" 
                      rules={[{ required: true, message: 'Please enter the degree!' }]}
                    >
                      <Input 
                        size="large"
                        placeholder="Bachelor's Degree in Nursing" 
                        style={{ borderRadius: '8px' }}
                      />
                </Form.Item>
              </Col>
                  <Col xs={24} md={8}>
                    <Form.Item 
                      name="keywords" 
                      label="Keywords" 
                      rules={[{ required: true, message: 'Please enter the keywords!' }]}
                    >
                      <Input 
                        size="large"
                        placeholder="Operating Room, NY (comma separated)" 
                        style={{ borderRadius: '8px' }}
                      />
                </Form.Item>
              </Col>
            </Row>
              </Card>
            </Col>

            {/* Schedule Card */}
            <Col span={24}>
              <Card 
                title={
                  <Space>
                    <CalendarOutlined />
                    Schedule & Timing
                  </Space>
                }
                style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={8}>
                    <Form.Item 
                      name="startDate" 
                      label="Start Date" 
                      rules={[{ required: true, message: 'Please select the start date!' }]}
                    >
                      <DatePicker 
                        size="large"
                        style={{ width: '100%', borderRadius: '8px' }} 
                        placeholder="Select start date"
                      />
                </Form.Item>
              </Col>
                  <Col xs={24} md={8}>
                    <Form.Item 
                      name="endDate" 
                      label="End Date" 
                      rules={[{ required: true, message: 'Please select the end date!' }]}
                    >
                      <DatePicker 
                        size="large"
                        style={{ width: '100%', borderRadius: '8px' }} 
                        placeholder="Select end date"
                      />
                </Form.Item>
              </Col>
                  <Col xs={24} md={8}>
                    <Form.Item 
                      name="hoursPerDay" 
                      label="Hours Per Day" 
                      rules={[{ required: true, message: 'Please enter the hours per day!' }]}
                    >
                      <InputNumber 
                        size="large"
                        placeholder="12" 
                        style={{ width: '100%', borderRadius: '8px' }}
                        min={1}
                        max={24}
                        suffix={<ClockCircleOutlined />}
                      />
                </Form.Item>
              </Col>
            </Row>

                <Row gutter={[16, 16]}>
                  <Col xs={24} md={8}>
                    <Form.Item 
                      name="daysPerWeek" 
                      label="Days Per Week" 
                      rules={[{ required: true, message: 'Please select days per week!' }]}
                    >
                      <Select size="large" placeholder="Select days" style={{ borderRadius: '8px' }}>
                        <Option value="3">3 Days</Option>
                        <Option value="5">5 Days</Option>
                        <Option value="6">6 Days</Option>
                  </Select>
                </Form.Item>
              </Col>
                  <Col xs={24} md={8}>
                    <Form.Item 
                      name="float" 
                      label="Float Position" 
                      rules={[{ required: true, message: 'Please select float!' }]}
                    >
                      <Select size="large" placeholder="Select float" style={{ borderRadius: '8px' }}>
                    <Option value="no">No</Option>
                    <Option value="yes">Yes</Option>
                  </Select>
                </Form.Item>
              </Col>
                  <Col xs={24} md={8}>
                    <Form.Item 
                      name="shift" 
                      label="Shift" 
                      rules={[{ required: true, message: 'Please select shift!' }]}
                    >
                      <Select size="large" placeholder="Select shift" style={{ borderRadius: '8px' }}>
                        <Option value="day">Day Shift</Option>
                        <Option value="night">Night Shift</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="scheduleNotes" label="Schedule Notes">
                  <Input.TextArea 
                    rows={3} 
                    placeholder="Any additional schedule information..."
                    style={{ borderRadius: '8px' }}
                  />
            </Form.Item>
              </Card>
            </Col>

            {/* Address Card */}
            <Col span={24}>
              <Card 
                title={
                  <Space>
                    <EnvironmentOutlined />
                    Work Location
                  </Space>
                }
                style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <Form.Item 
                      name="street" 
                      label="Street Address" 
                      rules={[{ required: true, message: 'Please enter the street!' }]}
                    >
                      <Input size="large" placeholder="123 Main Street" style={{ borderRadius: '8px' }} />
                </Form.Item>
              </Col>
                  <Col xs={24} md={12}>
                    <Form.Item 
                      name="city" 
                      label="City" 
                      rules={[{ required: true, message: 'Please enter the city!' }]}
                    >
                      <Input size="large" placeholder="San Francisco" style={{ borderRadius: '8px' }} />
                </Form.Item>
              </Col>
                </Row>

                <Row gutter={[16, 16]}>
                  <Col xs={24} md={16}>
                    <Form.Item 
                      name="state" 
                      label="State" 
                      rules={[{ required: true, message: 'Please enter the state!' }]}
                    >
                      <Select 
                        size="large" 
                        placeholder="Select state"
                        style={{ borderRadius: '8px' }}
                        showSearch
                        filterOption={(input, option) =>
                          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                      >
                    {states.map(state => (
                      <Option key={state} value={state}>{state}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
                  <Col xs={24} md={8}>
                    <Form.Item 
                      name="zipCode" 
                      label="ZIP Code" 
                      rules={[{ required: true, message: 'Please enter the zip code!' }]}
                    >
                      <Input size="large" placeholder="94105" style={{ borderRadius: '8px' }} />
                </Form.Item>
              </Col>
            </Row>
              </Card>
            </Col>

            {/* Salary Details Card */}
            <Col span={24}>
              <Card 
                title={
                  <Space>
                    <DollarOutlined />
                    Compensation Package
                  </Space>
                }
                style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={8}>
                    <Form.Item 
                      name="baseSalary" 
                      label="Base Salary (Hourly)" 
                      rules={[{ required: true, message: 'Please enter the base salary!' }]}
                    >
                  <InputNumber
                        size="large"
                        placeholder="120"
                    min={0}
                        style={{ width: '100%', borderRadius: '8px' }}
                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  />
                </Form.Item>
              </Col>
                  <Col xs={24} md={8}>
                    <Form.Item 
                      name="callBackSalary" 
                      label="Call Back Salary (Hourly)" 
                      rules={[{ required: true, message: 'Please enter the call back salary!' }]}
                    >
                  <InputNumber
                        size="large"
                        placeholder="140"
                    min={0}
                        style={{ width: '100%', borderRadius: '8px' }}
                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  />
                </Form.Item>
              </Col>
                  <Col xs={24} md={8}>
                    <Form.Item 
                      name="holidaySalary" 
                      label="Holiday Salary (Hourly)" 
                      rules={[{ required: true, message: 'Please enter the holiday salary!' }]}
                    >
                  <InputNumber
                        size="large"
                        placeholder="220"
                    min={0}
                        style={{ width: '100%', borderRadius: '8px' }}
                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  />
                </Form.Item>
              </Col>
            </Row>

                <Row gutter={[16, 16]}>
                  <Col xs={24} md={8}>
                    <Form.Item 
                      name="onCallSalary" 
                      label="On Call Salary (Hourly)" 
                      rules={[{ required: true, message: 'Please enter the on call salary!' }]}
                    >
                  <InputNumber
                        size="large"
                        placeholder="150"
                    min={0}
                        style={{ width: '100%', borderRadius: '8px' }}
                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  />
                </Form.Item>
              </Col>
                  <Col xs={24} md={8}>
                    <Form.Item 
                      name="overtimeSalary" 
                      label="Overtime Salary (Hourly)" 
                      rules={[{ required: true, message: 'Please enter the overtime salary!' }]}
                    >
                  <InputNumber
                        size="large"
                        placeholder="240"
                    min={0}
                        style={{ width: '100%', borderRadius: '8px' }}
                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  />
                </Form.Item>
              </Col>
                  <Col xs={24} md={8}>
                    <Form.Item 
                      name="regularSalary" 
                      label="Regular Salary (Hourly)" 
                      rules={[{ required: true, message: 'Please enter the regular salary!' }]}
                    >
                  <InputNumber
                        size="large"
                        placeholder="120"
                    min={0}
                        style={{ width: '100%', borderRadius: '8px' }}
                    formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  />
                </Form.Item>
              </Col>
            </Row>
              </Card>
            </Col>

            {/* Job Details Card */}
            <Col span={24}>
              <Card 
                title={
                  <Space>
                    <SettingOutlined />
                    Assignment Details
                  </Space>
                }
                style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <Form.Item 
                      name="type" 
                      label="Assignment Type" 
                      rules={[{ required: true, message: 'Please select the job type!' }]}
                    >
                      <Select size="large" placeholder="Select type" style={{ borderRadius: '8px' }}>
                    <Option value="contract">Contract</Option>
                        <Option value="per-diem">Per-Diem</Option>
                        <Option value="locum">Locum</Option>
                        <Option value="perm">Permanent</Option>
                  </Select>
                </Form.Item>
              </Col>
                  <Col xs={24} md={12}>
                    <Form.Item 
                      name="unit" 
                      label="Unit/Specialty" 
                      rules={[{ required: true, message: 'Please select the unit!' }]}
                    >
                      <Select 
                        size="large" 
                        placeholder="Select unit"
                        style={{ borderRadius: '8px' }}
                        showSearch
                        filterOption={(input, option) =>
                          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                      >
                        <Option value="Intensive care unit">Intensive Care Unit (ICU)</Option>
                        <Option value="Emergency Department">Emergency Department (ED)</Option>
                        <Option value="NICU">Neonatal Intensive Care Unit (NICU)</Option>
                        <Option value="Operating rooms">Operating Rooms (OR)</Option>
                        <Option value="PACU">Post-Anesthesia Care Unit (PACU)</Option>
                        <Option value="Pediatrics">Pediatrics</Option>
                        <Option value="Oncology">Oncology</Option>
                    <Option value="Surgery">Surgery</Option>
                        <Option value="Internal medicine">Internal Medicine</Option>
                        <Option value="Psychiatry">Psychiatry</Option>
                    <Option value="Radiology">Radiology</Option>
                    <Option value="Hematology">Hematology</Option>
                    <Option value="Orthopedics">Orthopedics</Option>
                        <Option value="Physical therapy">Physical Therapy</Option>
                        <Option value="Burn">Burn Unit</Option>
                    <Option value="Emergency Medicine">Emergency Medicine</Option>
                    <Option value="Gynecology">Gynecology</Option>
                    <Option value="Pathology">Pathology</Option>
                        <Option value="Outpatient department">Outpatient Department</Option>
                    <Option value="Perioperative">Perioperative</Option>
                    <Option value="Anesthetics">Anesthetics</Option>
                        <Option value="Infant care Unit">Infant Care Unit</Option>
                    <Option value="Pharmacy">Pharmacy</Option>
                        <Option value="Long-term care">Long-term Care</Option>
                        <Option value="Home care">Home Care</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
              </Card>
            </Col>

            {/* Mandatory Documents Card */}
            <Col span={24}>
              <Card 
                title={
                  <Space>
                    <FileTextOutlined />
                    Required Documents
                    <Tooltip title="Select documents that applicants must provide">
                      <InfoCircleOutlined style={{ color: '#999' }} />
                    </Tooltip>
                  </Space>
                }
                style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
              >
                {mandatoryDocuments.length === 0 && (
                  <Alert
                    message="No mandatory documents selected"
                    description="Select the documents that applicants must provide for this assignment."
                    type="info"
                    showIcon
                    style={{ marginBottom: '16px', borderRadius: '8px' }}
                  />
                )}
                
                <Form.Item label="Select Required Documents">
              <Checkbox.Group
                    options={Object.values(DocumentType).map(doc => ({
                      label: doc.replace(/-/g, ' '),
                      value: doc
                    }))}
                    value={mandatoryDocuments}
                onChange={handleDocumentChange}
              />
            </Form.Item>
                
                {mandatoryDocuments.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <Text strong>Selected Documents:</Text>
                    <div style={{ marginTop: '8px' }}>
                      {mandatoryDocuments.map(doc => (
                        <Tag key={doc} color="blue" style={{ margin: '4px' }}>
                          {doc.replace(/-/g, ' ')}
                        </Tag>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Action Buttons */}
          <div style={{ 
            marginTop: '32px', 
            textAlign: 'center',
            padding: '24px',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <Space size="large">
              <Button 
                size="large"
                onClick={() => form.resetFields()}
                style={{ 
                  height: '50px',
                  padding: '0 30px',
                  borderRadius: '25px',
                  fontSize: '16px'
                }}
              >
                <SaveOutlined />
                Save as Draft
          </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                size="large"
                icon={<EyeOutlined />}
                style={{ 
                  height: '50px',
                  padding: '0 40px',
                  borderRadius: '25px',
                  fontSize: '16px',
                  fontWeight: '600',
                  background: 'linear-gradient(135deg, #006971 0%, #004d54 100%)',
                  border: 'none',
                  boxShadow: '0 4px 12px rgba(0, 105, 113, 0.3)'
                }}
              >
                Preview & {isEditMode ? 'Update' : 'Publish'}
          </Button>
            </Space>
          </div>
      </Form>

        {/* Preview Modal */}
      <Modal
        visible={previewVisible}
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Space>
                <EyeOutlined style={{ color: '#006971' }} />
                <span style={{ color: '#006971', fontWeight: '600' }}>Assignment Preview</span>
              </Space>
              <Tag color="blue" style={{ marginRight: '50px' }}>{jobData?.code}</Tag>
            </div>
          }
        onCancel={() => setPreviewVisible(false)}
          width={1000}
          style={{ top: 20 }}
          bodyStyle={{ padding: '16px', maxHeight: '70vh', overflowY: 'auto' }}
        footer={[
          <Button key="edit" onClick={() => setPreviewVisible(false)}>
              <EditOutlined />
            Edit
          </Button>,
            <Button 
              key="publish" 
              type="primary" 
              loading={loading}
              onClick={handlePublish}
              style={{
                background: 'linear-gradient(45deg, #006971, #00a0a0)',
                border: 'none'
              }}
            >
              <SaveOutlined />
              {isEditMode ? 'Update Assignment' : 'Publish Assignment'}
          </Button>,
        ]}
      >
          <Row gutter={[16, 0]} style={{ margin: 0 }}>
            {/* Left Column */}
            <Col span={12} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Job Header */}
              <Card size="small" style={{ background: '#f8f9fa', marginBottom: 0 }}>
                <div>
                  <Text strong style={{ color: '#006971', fontSize: '16px' }}>{jobData?.jobTitle}</Text>
                  <div style={{ marginTop: '4px', fontSize: '13px', color: '#666' }}>
                    {jobData?.jobCategory} • {jobData?.numberOfPositions} Position{jobData?.numberOfPositions > 1 ? 's' : ''}
                  </div>
                </div>
              </Card>

              {/* Job Information */}
              <Card title={<span><BuildOutlined /> Job Details</span>} size="small" style={{ marginBottom: 0 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div><Text strong>Manager:</Text> {jobData?.manager}</div>
                  <div><Text strong>Department:</Text> {jobData?.department}</div>
                  <div><Text strong>Unit:</Text> {jobData?.unit}</div>
                  <div><Text strong>Type:</Text> <Tag color="blue" size="small">{jobData?.type}</Tag></div>
                </div>
                
                <div style={{ marginTop: '12px' }}>
                  <Text strong>Description:</Text>
                  <div style={{ 
                    marginTop: '4px', 
                    padding: '8px', 
                    background: '#f5f5f5', 
                    borderRadius: '4px',
                    fontSize: '12px',
                    lineHeight: '1.4'
                  }}>
                    {jobData?.jobDescription}
                  </div>
                </div>
              </Card>

              {/* Skills & Requirements */}
              <Card title="Skills & Requirements" size="small" style={{ marginBottom: 0 }}>
                <div style={{ marginBottom: '10px' }}>
                  <Text strong style={{ fontSize: '12px' }}>Required Skills:</Text>
                  <div style={{ marginTop: '4px' }}>
                    {jobData?.requiredSkills?.split(',').map((skill, index) => (
                      <Tag key={index} color="red" size="small" style={{ margin: '1px', fontSize: '11px' }}>
                        {skill.trim()}
                      </Tag>
                    ))}
                  </div>
                </div>
                
                <div style={{ marginBottom: '10px' }}>
                  <Text strong style={{ fontSize: '12px' }}>Preferred Skills:</Text>
                  <div style={{ marginTop: '4px' }}>
                    {jobData?.preferredSkills?.split(',').map((skill, index) => (
                      <Tag key={index} color="blue" size="small" style={{ margin: '1px', fontSize: '11px' }}>
                        {skill.trim()}
                      </Tag>
                    ))}
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px' }}>
                  <div><Text strong>Degree:</Text> {jobData?.degree}</div>
                  <div><Text strong>Languages:</Text> {jobData?.languages}</div>
                </div>
              </Card>

              {/* Required Documents */}
              <Card title={<span><FileTextOutlined /> Required Documents</span>} size="small" style={{ marginBottom: 0 }}>
                {mandatoryDocuments.length > 0 ? (
                  <div>
                    {mandatoryDocuments.map(doc => (
                      <Tag key={doc} color="green" size="small" style={{ margin: '1px', fontSize: '11px' }}>
                        {doc.replace(/-/g, ' ')}
                      </Tag>
                    ))}
                  </div>
                ) : (
                  <Text type="secondary" style={{ fontSize: '12px' }}>No specific documents required</Text>
                )}
              </Card>
            </Col>

            {/* Right Column */}
            <Col span={12} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Schedule & Timing */}
              <Card title={<span><CalendarOutlined /> Schedule & Timing</span>} size="small" style={{ marginBottom: 0 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px' }}>
                  <div><Text strong>Start:</Text> {jobData?.startDate?.format('MMM DD, YYYY')}</div>
                  <div><Text strong>End:</Text> {jobData?.endDate?.format('MMM DD, YYYY')}</div>
                  <div><Text strong>Hours/Day:</Text> {jobData?.hoursPerDay}h</div>
                  <div><Text strong>Days/Week:</Text> {jobData?.daysPerWeek}</div>
                  <div><Text strong>Shift:</Text> <Tag color={jobData?.shift === 'day' ? 'orange' : 'purple'} size="small">{jobData?.shift}</Tag></div>
                  <div><Text strong>Float:</Text> <Tag color={jobData?.float === 'yes' ? 'green' : 'default'} size="small">{jobData?.float}</Tag></div>
                </div>
                
                {jobData?.scheduleNotes && (
                  <div style={{ marginTop: '10px' }}>
                    <Text strong style={{ fontSize: '12px' }}>Notes:</Text>
                    <div style={{ fontSize: '11px', fontStyle: 'italic', color: '#666', marginTop: '2px' }}>
                      {jobData?.scheduleNotes}
                    </div>
                  </div>
                )}
              </Card>

              {/* Location */}
              <Card title={<span><EnvironmentOutlined /> Location</span>} size="small" style={{ marginBottom: 0 }}>
                <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
                  {jobData?.street}<br/>
                  {jobData?.city}, {jobData?.state} {jobData?.zipCode}
                </div>
              </Card>

              {/* Compensation */}
              <Card title={<span><DollarOutlined /> Compensation</span>} size="small" style={{ marginBottom: 0 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                  <div style={{ textAlign: 'center', padding: '6px', background: '#f0f9ff', borderRadius: '4px' }}>
                    <div style={{ fontSize: '10px', color: '#666' }}>Base Rate</div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#006971' }}>
                      ${jobData?.baseSalary}/hr
                    </div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '6px', background: '#f0f9ff', borderRadius: '4px' }}>
                    <div style={{ fontSize: '10px', color: '#666' }}>Overtime</div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#006971' }}>
                      ${jobData?.overtimeSalary}/hr
                    </div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '4px', background: '#fafafa', borderRadius: '4px' }}>
                    <div style={{ fontSize: '9px', color: '#666' }}>Holiday</div>
                    <div style={{ fontSize: '12px', fontWeight: '500' }}>
                      ${jobData?.holidaySalary}/hr
                    </div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '4px', background: '#fafafa', borderRadius: '4px' }}>
                    <div style={{ fontSize: '9px', color: '#666' }}>On-Call</div>
                    <div style={{ fontSize: '12px', fontWeight: '500' }}>
                      ${jobData?.onCallSalary}/hr
                    </div>
                  </div>
                </div>
              </Card>

              {/* Additional Details */}
              <Card title="Additional Details" size="small" style={{ marginBottom: 0 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12px' }}>
                  <div><Text strong>Status Reason:</Text> {jobData?.statusReason}</div>
                  <div><Text strong>Reason:</Text> {jobData?.reasonDescription}</div>
                  <div>
                    <Text strong>Keywords:</Text>
                    <div style={{ marginTop: '2px' }}>
                      {jobData?.keywords?.split(',').map((keyword, index) => (
                        <Tag key={index} size="small" style={{ margin: '1px', fontSize: '10px' }}>
                          {keyword.trim()}
                        </Tag>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
      </Modal>
      </div>
    </div>
  );
};

export default PostJobScreen;
